import * as React from 'react';
import type { CalendarSurfaceStore } from '../../index';
import { ResizeHandle, ResizeHandleDirection } from 'owa-resize-handle';
import { SurfaceConstants, toPixelString } from 'owa-calendar-surface-core';
import { updateCollapsedAllDayWellHeight } from 'owa-calendar-surface-options-utils';
import { useCustomTimeout } from 'owa-react-hooks/lib/useCustomTimeout';

const styles = require('./AllDayWellResizeHooks.scss');
const ATTRIBUTE_MAX_HEIGHT = 'data-max-height';

const TIMER_DELAY = 100;
const MIN_WELL_HEIGHT = 2 * SurfaceConstants.AllDayRowHeight;

export interface AllDayWellResizeHooksProps {
    store: CalendarSurfaceStore;
}

export default function useAllDayWellResizeHooks(props: AllDayWellResizeHooksProps) {
    let container = null as React.MutableRefObject<HTMLElement>;
    let { current: placeholderElement } = React.useRef<HTMLElement | null>(null);
    const resizeHandleRef = React.useRef<HTMLDivElement>(null);
    const resizeHandleFirstChildRef = React.useRef<HTMLElement>(null);
    const resizeHandleLastChildRef = React.useRef<HTMLElement>(null);

    let { current: initialMaxHeight } = React.useRef<string>('');
    let { current: isDragging } = React.useRef<boolean>(false);

    const [setUpdateHeightTimer] = useCustomTimeout();
    const [setAttachTimer] = useCustomTimeout();

    const resizeHandleFirstChildRefFunction = (el: HTMLElement) => {
        resizeHandleFirstChildRef.current = el;
    };

    const resizeHandleLastChildRefFunction = (el: HTMLElement) => {
        resizeHandleLastChildRef.current = el;
    };

    const setContainerStyles = (allDayWellHeight: number) => {
        // don't want to interfere with the component setting 'height', so we can
        // override by forcing both min and max height to the desired height
        container.current.style.maxHeight = toPixelString(allDayWellHeight);
        container.current.style.minHeight = toPixelString(allDayWellHeight);
        placeholderElement.style.maxHeight = toPixelString(allDayWellHeight);
        placeholderElement.style.minHeight = toPixelString(allDayWellHeight);

        // apply the dragging class if it isn't already there - DOMTokenList filters dupes
        container.current.classList.add(styles.draggingContainer);
    };

    const clearContainerStyles = () => {
        container.current.classList.remove(styles.draggingContainer);
        container.current.style.maxHeight = initialMaxHeight;
        container.current.style.minHeight = '';
        placeholderElement.style.maxHeight = '';
        placeholderElement.style.minHeight = '';
    };

    /**
     * Function to run on every update of the drag
     */
    const onResizing = (allDayWellHeight: number) => {
        isDragging = true;
        const snappedHeight = calculatedSnappedHeight(allDayWellHeight);

        setUpdateHeightTimer(() => {
            updateCollapsedAllDayWellHeight(Math.max(snappedHeight, MIN_WELL_HEIGHT));
        }, TIMER_DELAY);

        if (container.current && placeholderElement) {
            setContainerStyles(allDayWellHeight);
            // resizeHandle tries to move it, but since we're resizing children, fix in place
            resizeHandleRef.current.style.top = '0';
        }
    };

    /**
     * Function to run when the drag handle is released
     */
    const onResize = (allDayWellHeight: number) => {
        isDragging = false;
        clearContainerStyles();

        // the number coming from newBounds needs to be snapped to a AllDayRowHeight-multiple value
        const newHeight = calculatedSnappedHeight(allDayWellHeight);
        updateCollapsedAllDayWellHeight(Math.max(newHeight, MIN_WELL_HEIGHT));
    };

    const findPlaceholderElement = (element: HTMLElement): HTMLElement => {
        let el = element;
        while (el != document.body && !placeholderElement) {
            for (let i = 0; i < el.children.length; i++) {
                const child = el.children[i];
                const attribute = child.getAttribute(
                    SurfaceConstants.allDayPlaceholderAttributeName
                );
                if (attribute) {
                    return child as HTMLElement;
                }
            }
            el = el.parentElement;
        }

        return null;
    };

    const initializeStyles = () => {
        if (container.current) {
            // wrap in setTimeout to prevent layout recalulation during rendering
            setAttachTimer(() => {
                placeholderElement = null; // initialize to null so function can find it
                placeholderElement = findPlaceholderElement(container.current);

                if (
                    !isDragging &&
                    resizeHandleFirstChildRef.current &&
                    resizeHandleLastChildRef.current
                ) {
                    initialMaxHeight = window.getComputedStyle(container.current).maxHeight;

                    // max height is the minimum height of the container's data-max-height and the
                    // container's calculated max-height, rounded downwards to the nearest snapped height
                    const maxHeight = roundDownToSnappedHeight(
                        Math.min(
                            parseInt(container.current.getAttribute(ATTRIBUTE_MAX_HEIGHT), 10),
                            parseInt(initialMaxHeight, 10)
                        )
                    );

                    resizeHandleFirstChildRef.current.setAttribute(
                        ATTRIBUTE_MAX_HEIGHT,
                        maxHeight + ''
                    );

                    resizeHandleLastChildRef.current.setAttribute(
                        ATTRIBUTE_MAX_HEIGHT,
                        maxHeight + ''
                    );

                    // set the current height of bottom child to maxHeight - height of parent
                    resizeHandleLastChildRef.current.style.height = `calc(${maxHeight}px - 100%)`;
                }
            }, 0);
        }
    };

    const children = React.useMemo(() => {
        if (props.store.isAllDayWellExpanded) {
            return null;
        }

        return (
            <div className={styles.root}>
                <div
                    ref={resizeHandleFirstChildRefFunction}
                    data-min-height={MIN_WELL_HEIGHT}
                    className={styles.resizeHandleFirstChild}
                />
                <ResizeHandle
                    ref={resizeHandleRef}
                    onClick={onMouseEvent}
                    className={styles.resizeHandle}
                    direction={ResizeHandleDirection.horizontal}
                    onResize={onResize}
                    onResizing={onResizing}
                />
                <div
                    ref={resizeHandleLastChildRefFunction}
                    className={styles.resizeHandleLastChild}
                />
            </div>
        );
    }, [props.store.isAllDayWellExpanded]);

    return React.useCallback(
        (ref: React.MutableRefObject<HTMLElement>) => {
            container = ref;
            initializeStyles();
            return {
                children: children,
                handlers: {},
            };
        },
        [children]
    );
}

/**
 * calculates the closest snapped height value
 * @param requestedHeight
 */
function calculatedSnappedHeight(requestedHeight: number): number {
    return (
        Math.round(requestedHeight / SurfaceConstants.AllDayRowHeight) *
        SurfaceConstants.AllDayRowHeight
    );
}

/**
 * calculates the closest snapped height value that's lower than the given height
 * @param requestedHeight
 */
function roundDownToSnappedHeight(requestedHeight: number): number {
    return (
        Math.floor(requestedHeight / SurfaceConstants.AllDayRowHeight) *
        SurfaceConstants.AllDayRowHeight
    );
}

function onMouseEvent(ev: React.MouseEvent<HTMLElement>) {
    ev.stopPropagation();
}
